import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { Subject } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { ResizeSyncService } from '@ui/tablo-generator/resize-sync.service';
@Directive({
    selector: '[libFitIntoContainer]',
    standalone: false
})
export class FitIntoContainerDirective
  extends AliveState
  implements OnInit, OnChanges, OnDestroy
{
  private static readonly fontWaitIntervalMs = 100;
  private static readonly maxFontWaitMs = 3000;
  private static readonly minFontSizePx = 1;
  private static readonly maxResizeIterations = 300;

  get maxFontSize(): number {
    return this._maxFontSize;
  }
  @Input() scale = 1;
  @Input() svgTextElement: SVGTextElement | null = null;

  @Input() set maxFontSize(value: number) {
    this._maxFontSize = value;
    this.element.nativeElement.style.fontSize = `${value}px`;
    if (this.svgTextElement) {
      this.svgTextElement.setAttribute('font-size', `${value}px`);
    }

    this.changesMade.next(undefined);
  }
  get verticalAlign(): 'top' | 'center' | 'bottom' {
    return this._verticalAlign;
  }

  @Input() set verticalAlign(value: 'top' | 'center' | 'bottom') {
    this._verticalAlign = value;
    this.completed.emit();
  }
  private changes: MutationObserver;
  verticalUpdate = false;
  changesMade = new Subject();
  @Input() elementId: string;
  @Output() completed = new EventEmitter<void>();
  @Output() allCompleted = new EventEmitter<void>();
  private _maxFontSize = 50;
  fixInProgress = false;
  @Input() set resized(resized: boolean) {
    this.resized$.next(undefined);
  }

  resized$ = new Subject();
  private _verticalAlign: 'top' | 'center' | 'bottom' = 'top';
  private destroyed = false;
  private fontWaitRunId = 0;
  constructor(
    private element: ElementRef<HTMLElement>,
    private resizeSyncService: ResizeSyncService,
  ) {
    super();
    this.element.nativeElement.style.top = 'auto';
    // element.nativeElement.parentElement.style.overflow = 'auto';
    this.changes = new MutationObserver((mutations: MutationRecord[]) => {
      // console.log(mutations.filter((v) => v.attributeName !== 'style'));
      if (
        !this.verticalUpdate &&
        mutations.filter((v) => v.attributeName !== 'style').length > 0
      ) {
        this.fixInProgress = false;
        this.changesMade.next(undefined);
      }
    });

    this.changes.observe(this.element.nativeElement, {
      attributes: true,
      childList: true,
      characterData: true,
    });
  }

  isFromEnlarging = false;
  ngOnInit() {
    this.element.nativeElement.style.opacity = '0';
    this.subscribeWhileAlive(
      this.resized$.pipe(
        tap(() => {
          this.changesMade.next(undefined);
        }),
      ),
      this.completed.pipe(
        tap(() => {
          const parentElement = this.element.nativeElement.parentElement;
          if (!parentElement || this.destroyed) {
            return;
          }
          this.element.nativeElement.style.opacity = '1';
          this.verticalUpdate = true;
          switch (this._verticalAlign) {
            case 'top':
              parentElement.style.top = '0';
              parentElement.style.bottom = 'auto';
              break;
            case 'center':
              parentElement.style.top = `calc(50% - ${
                this.element.nativeElement.clientHeight / 2
              }px)`;
              parentElement.style.bottom = 'auto';
              break;
            case 'bottom':
              parentElement.style.top = `calc(100% - ${this.element.nativeElement.clientHeight}px)`;
              parentElement.style.bottom = '0';
              break;
          }
          this.verticalUpdate = false;
          this.allCompleted.emit();
        }),
      ),
      this.resizeSyncService.sizeUpdated.pipe(
        // tap((x) => console.log(x, this.elementId)),
        filter((id) => id === this.elementId),
        tap(() => this.changesMade.next(undefined)),
      ),
      this.changesMade.pipe(
        debounceTime(100),
        filter(() => !this.fixInProgress),
        switchMap(async () => this.waitForFontsReady(++this.fontWaitRunId)),
        filter((canContinue) => canContinue),
        tap(() => {
          const parentElement = this.element.nativeElement.parentElement;
          if (!parentElement || this.destroyed) {
            this.fixInProgress = false;
            return;
          }
          parentElement.style.top = 'auto';
          parentElement.style.bottom = 'auto';
          this.fixInProgress = true;
          // console.log(`
          //
          // CONTAINER RESIZED
          //
          // `);
          let realTextSize = this.measureTextSize();
          if (this.isOverflowing(realTextSize, parentElement)) {
            // Check if text is multi-line by comparing scrollHeight to clientHeight
            // or by checking if the text would naturally wrap due to container width
            const hasMultipleLines =
              realTextSize.height > this.element.nativeElement.clientHeight ||
              realTextSize.width >= parentElement.clientWidth;

            let fontSize = this.getCurrentFontSize();
            let remainingIterations =
              FitIntoContainerDirective.maxResizeIterations;
            while (
              this.isOverflowing(realTextSize, parentElement) &&
              fontSize > FitIntoContainerDirective.minFontSizePx &&
              remainingIterations-- > 0
            ) {
              fontSize = Math.max(
                FitIntoContainerDirective.minFontSizePx,
                fontSize - 1,
              );
              this.applyFontSize(fontSize);

              // For multi-line text, we need to check after each adjustment
              // to ensure proper line wrapping and height calculation
              if (hasMultipleLines) {
                // Force a reflow to ensure accurate measurements
                void this.element.nativeElement.offsetHeight;
              }

              // Recalculate realTextSize after adjusting font size
              realTextSize = this.measureTextSize();
            }
            console.log({
              fontSize: this.getCurrentFontSize(),
              clientWidth: realTextSize.width,
              parentWidth: parentElement.clientWidth,
              clientHeight: realTextSize.height,
              parentHeight: parentElement.clientHeight,
            });
            this.completed.emit();
            setTimeout(() => {
              if (!this.destroyed) {
                this.fixInProgress = false;
              }
            }, 200);
          } else {
            if (!this.isFromEnlarging) {
              let fontSize = this.getCurrentFontSize();

              // Check if text is multi-line by comparing scrollHeight to clientHeight
              // or by checking if the text would naturally wrap due to container width
              realTextSize = this.measureTextSize();

              const hasMultipleLines =
                this.element.nativeElement.scrollHeight >
                  this.element.nativeElement.clientHeight ||
                realTextSize.width >= parentElement.clientWidth;

              // For multi-line text, we need a different approach to determine if it fits
              const checkFits = () => {
                // Recalculate realTextSize for current font size
                realTextSize = this.measureTextSize();

                // For multi-line text, we need to ensure all content fits properly
                if (hasMultipleLines) {
                  // For text that wraps naturally, we need to check if it fits within the container
                  // by comparing scrollHeight to parentElement.clientHeight
                  return (
                    realTextSize.width <= parentElement.clientWidth &&
                    realTextSize.height <= parentElement.clientHeight &&
                    this.element.nativeElement.scrollHeight <=
                      parentElement.clientHeight
                  );
                } else {
                  // For single-line text, the original check is fine
                  return (
                    realTextSize.width < parentElement.clientWidth ||
                    realTextSize.height < parentElement.clientHeight
                  );
                }
              };

              while (checkFits() && fontSize < this._maxFontSize) {
                fontSize++;
                this.applyFontSize(fontSize);

                // Force a reflow to ensure accurate measurements for multi-line text
                if (hasMultipleLines) {
                  void this.element.nativeElement.offsetHeight;
                }
              }

              // If we went too far and the text no longer fits, step back one size
              if (!checkFits() && fontSize > FitIntoContainerDirective.minFontSizePx) {
                fontSize--;
                this.applyFontSize(fontSize);
              }

              this.changesMade.next(undefined);
              this.fixInProgress = false;
              this.isFromEnlarging = true;
            } else {
              this.isFromEnlarging = false;
              this.fixInProgress = false;
              this.completed.emit();
            }
          }
        }),
      ),
    );
  }

  private getCurrentFontSize(): number {
    const fontSize = parseFloat(
      getComputedStyle(this.element.nativeElement).fontSize,
    );

    return Number.isFinite(fontSize) && fontSize > 0
      ? fontSize
      : Math.max(this._maxFontSize, FitIntoContainerDirective.minFontSizePx);
  }

  private applyFontSize(fontSize: number): void {
    const nextFontSize = Math.max(
      FitIntoContainerDirective.minFontSizePx,
      fontSize,
    );
    this.element.nativeElement.style.fontSize = `${nextFontSize}px`;
    if (this.svgTextElement) {
      this.svgTextElement.setAttribute('font-size', `${nextFontSize}px`);
    }
  }

  private async waitForFontsReady(runId: number): Promise<boolean> {
    const fontSet = (document as any).fonts as
      | { check?: (font: string) => boolean }
      | undefined;
    if (!fontSet?.check) {
      return true;
    }

    let waitedMs = 0;
    while (!this.destroyed && this.fontWaitRunId === runId) {
      try {
        if (
          fontSet.check(
            `1px ${getComputedStyle(this.element.nativeElement).fontFamily}`,
          )
        ) {
          return true;
        }
      } catch {
        return true;
      }

      if (waitedMs >= FitIntoContainerDirective.maxFontWaitMs) {
        return true;
      }

      await new Promise((resolve) =>
        setTimeout(resolve, FitIntoContainerDirective.fontWaitIntervalMs),
      );
      waitedMs += FitIntoContainerDirective.fontWaitIntervalMs;
    }

    return false;
  }

  private measureTextSize() {
    try {
      const range = document.createRange();
      range.selectNodeContents(this.element.nativeElement);
      const rect = range.getBoundingClientRect();
      const scale = this.scale || 1;

      return {
        x: rect.x / scale,
        y: rect.y / scale,
        width: rect.width / scale,
        height: rect.height / scale,
      };
    } catch {
      return {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      };
    }
  }

  private isOverflowing(
    realTextSize: { width: number; height: number },
    parentElement: HTMLElement,
  ): boolean {
    return (
      realTextSize.width > parentElement.clientWidth ||
      realTextSize.height > parentElement.clientHeight
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.fontWaitRunId += 1;
    this.changes.disconnect();
    super.ngOnDestroy();
  }
}
